import { useEffect, useRef } from "react";
import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    codeBlockPlugin,
    codeMirrorPlugin,
    diffSourcePlugin,
    DiffSourceToggleWrapper,
    headingsPlugin,
    InsertTable,
    listsPlugin,
    ListsToggle,
    MDXEditorMethods,
    quotePlugin,
    tablePlugin,
    thematicBreakPlugin,
    toolbarPlugin,
    UndoRedo,
} from "@mdxeditor/editor";
import { ForwardRefEditor } from "./ForwardRefEditor";

/**
 * MDX escapes markdown-significant characters on export (`object_id` becomes
 * `object\_id`, `[label]` becomes `\[label]`). In a prompt those are plain
 * identifiers and citation labels, and `{object\_id}` would no longer match the
 * placeholder the backend fills in, so the escapes are removed again. It also
 * turns the last trailing space of a line into `&#x20;`.
 */
export const normalizeRichFieldMarkdown = (markdown: string): string =>
    markdown
        .replace(/\\([_\[])/g, "$1")
        .replace(/&#x20;/g, " ");

/** The editor configuration shared by RichField and its round-trip test. */
export const richFieldPlugins = () => [
    headingsPlugin(),
    listsPlugin(),
    quotePlugin(),
    thematicBreakPlugin(),
    tablePlugin({ tablePipeAlign: false, tableCellPadding: false }),
    codeBlockPlugin({ defaultCodeBlockLanguage: "" }),
    codeMirrorPlugin({ codeBlockLanguages: { "": "Plain Text" } }),
    diffSourcePlugin({ viewMode: "rich-text" }),
    toolbarPlugin({
        toolbarClassName: "richtext-toolbar",
        toolbarContents: () => (
            <DiffSourceToggleWrapper>
                <UndoRedo />
                <BlockTypeSelect />
                <BoldItalicUnderlineToggles />
                <ListsToggle />
                <InsertTable />
            </DiffSourceToggleWrapper>
        ),
    }),
];

/**
 * A large, always-editable markdown field for long prompts (Settings -> KVP).
 * Place it in an `.entry-field-rich` band, which supplies the inset its toolbar
 * spans (see lists.css).
 *
 * `value` and `onChange` are the stored markdown string. onChange only fires for
 * real edits: the editor's initial normalisation of what it was given is ignored,
 * so merely opening an entry never rewrites it. The toolbar has a Rich text /
 * Source switch; Source shows the exact stored characters.
 *
 * BEWARE (same as the entry editor in UI/tools.tsx): MDX turns the last space of
 * trailing whitespace into `&#x20;`.
 */
export const RichField = ({
    value,
    onChange,
    minHeightRem = 20,
    disabled,
    placeholder,
}: {
    value: string;
    onChange: (value: string) => void;
    minHeightRem?: number;
    disabled?: boolean;
    placeholder?: string;
}) => {
    const editorRef = useRef<MDXEditorMethods>(null);
    const focusedRef = useRef(false);

    // Push outside changes (reload, switching entry) into the editor, but never
    // while the user is typing in it, which would move their caret.
    useEffect(() => {
        if (focusedRef.current) return;
        const editor = editorRef.current;
        if (editor && editor.getMarkdown() !== value) editor.setMarkdown(value);
    }, [value]);

    return (
        <div
            className="richtext richtext-editing"
            style={{ minHeight: `${minHeightRem}rem` }}
            onFocusCapture={() => { focusedRef.current = true; }}
            onBlurCapture={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                    focusedRef.current = false;
                }
            }}
        >
            <ForwardRefEditor
                ref={editorRef}
                markdown={value}
                readOnly={disabled}
                placeholder={placeholder}
                plugins={richFieldPlugins()}
                onChange={(next, isInit) => {
                    if (!isInit) onChange(normalizeRichFieldMarkdown(next));
                }}
                contentEditableClassName="richtext-content prose serif"
                suppressHtmlProcessing={false}
            />
        </div>
    );
};
